import { useRef, useEffect } from 'react'

import { FETCH_STATUS } from '../../../../shared/constants/fetchStatus.js'

import styles from './SvgOutlineLayer.module.scss'

/**
 * SvgOutlineLayer
 *
 * Injects an SVG outline overlay via `dangerouslySetInnerHTML` and imperatively
 * manages two post-mount side-effects:
 *
 * 1. **Frame groups** (ModelView only) — collects all `<g data-frame="N">` elements
 *    into `frameGroupsRef.current` immediately after the SVG is in the DOM.
 *    `useModelFrames` then drives frame switching via direct classList toggles
 *    on those elements — zero React state in the hot path.
 *
 * 2. **Interaction listeners** — calls `wireListeners(containerEl)` which returns
 *    a cleanup function.  Re-fires whenever `svgString` or `wireListeners` changes
 *    (i.e. on outline switch, view change, or when the interaction hook re-creates
 *    its callback due to data changes).
 *
 * Returns null while loading or on fetch error — the caller is responsible for
 * any loading/error UI above this layer.
 *
 * @param {object}                  props
 * @param {string|null}             props.svgString        — SVG markup from `useOutlineSvgFetch`
 * @param {string}                  props.svgStatus        — FETCH_STATUS value
 * @param {function}                props.wireListeners    — `(containerEl) => cleanupFn`; from an interaction hook
 * @param {React.MutableRefObject}  [props.frameGroupsRef] — optional; when provided, populated with
 *                                                            all `svg > g` elements (one per frame) after mount.
 *                                                            Pass null / omit for VisualizationView.
 * @param {React.MutableRefObject}  [props.currentIndexRef] — current frame index ref from `useModelFrames`;
 *                                                             used to apply the initial active CSS class when SVG first loads.
 * @param {string}                  [props.activeSvgClass]  — CSS class toggled on the active frame group
 *                                                             (must match the constant in ModelView; e.g. `'svg-frame--active'`)
 * @param {React.MutableRefObject}   [props.containerElRef]   — optional DOM ref to the injected SVG container
 */
export function SvgOutlineLayer({ svgString, svgStatus, wireListeners, frameGroupsRef, currentIndexRef, activeSvgClass, containerElRef = null }) {
  const containerRef = useRef(null)

  useEffect(() => {
    if (!containerElRef) return undefined

    containerElRef.current = containerRef.current

    return () => {
      if (containerElRef.current === containerRef.current) {
        containerElRef.current = null
      }
    }
  }, [containerElRef, svgString, svgStatus])

  // ── 1. Populate frameGroupsRef after SVG mounts ───────────────────────────
  // Generator SVGs use plain <svg > g> with no attributes.
  // We assign data-frame="N" imperatively so that:
  //   a) CSS rule `g[data-frame] { display:none }` hides all groups by default
  //   b) Interaction hooks can call `el.closest('g[data-frame]')` to identify the active frame
  //
  // After populating refs, we apply the initial active class so the correct
  // group is immediately visible (useModelFrames only updates on drag/rotate,
  // not on initial SVG mount).
  useEffect(() => {
    if (!frameGroupsRef || !containerRef.current || svgStatus !== FETCH_STATUS.READY) return

    const groups = Array.from(containerRef.current.querySelectorAll('svg > g'))
    groups.forEach((g, i) => {
      g.setAttribute('data-frame', String(i))
      if (activeSvgClass) {
        g.classList.toggle(activeSvgClass, i === (currentIndexRef?.current ?? 0))
      }
    })
    frameGroupsRef.current = groups
  }, [svgString, svgStatus, frameGroupsRef, currentIndexRef, activeSvgClass])

  // ── 2. Wire interaction listeners after SVG mounts ────────────────────────
  useEffect(() => {
    if (!wireListeners || !containerRef.current || svgStatus !== FETCH_STATUS.READY) return

    const cleanup = wireListeners(containerRef.current)
    return cleanup
  }, [svgString, svgStatus, wireListeners])

  // ── Render ────────────────────────────────────────────────────────────────
  if (!svgString || svgStatus !== FETCH_STATUS.READY) return null

  return (
    <div
      ref={containerRef}
      className={styles.svgOutlineLayer}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svgString }}
    />
  )
}
